// Convert value to string if it's a number
const stringifyKey = (key) => (typeof key === 'number' ? key.toString() : key)

// Convert all numbers in the key to strings
const keyToString = (key = []) => key.map(stringifyKey)

// Wrap key for additional processing
const key = (...args) => keyToString(args)

// Helper function to create 'lists' and 'list' keys
const createIndexesKeys = (...path) => ({
  all: key(...path),
  lists: key(...path, 'lists'),
  list: (query) => key(...path, 'lists', query),
})

const QueryKeys = {
  me: (id) => ({
    details: key('me', id, 'details'),
  }),

  auth: {
    code: (code) => key('auth', 'code', code),
  },

  // ===================== ADMIN =====================
  admin: {
    gapBoard: {
      providers: createIndexesKeys('admin', 'gap-board', 'providers'),
    },
    metrics: createIndexesKeys('admin', 'metrics'),
    cbos: {
      ...createIndexesKeys('admin', 'cbos'),
      id: (cboId) => ({
        details: key('admin', 'cbos', cboId, 'details'),
        contactUsers: createIndexesKeys('admin', 'cbos', cboId, 'contact-users'),
        providers: createIndexesKeys('admin', 'cbos', cboId, 'providers'),
      }),
    },
    providers: {
      ...createIndexesKeys('admin', 'providers'),
      cpas: createIndexesKeys('admin', 'providers', 'cpas'),
      id: (providerId) => ({
        details: key('admin', 'providers', providerId, 'details'),
        cbos: createIndexesKeys('admin', 'providers', providerId, 'cbos'),
        notes: createIndexesKeys('admin', 'providers', providerId, 'notes'),
        supervisedProviders: createIndexesKeys('admin', 'providers', providerId, 'supervised-providers'),
      }),
    },
    licenses: {
      ...createIndexesKeys('admin', 'licenses'),
      report: {
        all: key('admin', 'licenses', 'report'),
        overview: () => key('admin', 'licenses', 'report', 'overview'),
        providers: (query) => key('admin', 'licenses', 'report', 'providers', query),
        medicaidGaps: () => key('admin', 'licenses', 'report', 'medicaid-gaps'),
      },
    },
    systemUsers: {
      ...createIndexesKeys('admin', 'system-users'),
      id: (userId) => ({
        details: key('admin', 'system-users', userId, 'details'),
      }),
    },
    reports: {
      activePatientsVsPrescriptions: (filters) => key('admin', 'reports', 'active-patients-vs-prescriptions', filters),
      enrolledVsActivePatients: (filters) => key('admin', 'reports', 'enrolled-vs-active-patients', filters),
      cbosWeeklyKpis: (filters) => key('admin', 'reports', 'cbos-weekly-kpis', filters),
      stateWeeklyKpis: (filters) => key('admin', 'reports', 'state-weekly-kpis', filters),
      completedReschedulesFromNoShows: () => key('admin', 'reports', 'completed-reschedules-from-no-shows'),
      enrolledApptCompleted: () => key('admin', 'reports', 'enrolled-appt-completed'),
      enrollmentsAndServiceLineScheduled: () => key('admin', 'reports', 'enrollments-and-service-line-scheduled'),
      initialEncounters: (filters) => key('admin', 'reports', 'initial-encounters', filters),
      prepInitialScheduledFromDailyEnrollment: () => key('admin', 'reports', 'prep-initial-scheduled-from-daily-enrollment'),
      newVsTransferPrep: () => key('admin', 'reports', 'new-vs-transfer-prep'),
      prepInitialNoShows: () => key('admin', 'reports', 'prep-initial-no-shows'),
      userJourneys: {
        overview: (query) => key('admin', 'reports', 'user-journeys', 'overview', query),
        funnel: (query) => key('admin', 'reports', 'user-journeys', 'funnel', query),
        fieldDropOffs: (query) => key('admin', 'reports', 'user-journeys', 'field-drop-offs', query),
        unopenedVisits: (query) => key('admin', 'reports', 'user-journeys', 'unopened-visits', query),
        steps: (query) => key('admin', 'reports', 'user-journeys', 'steps', query),
        journeys: (query) => key('admin', 'reports', 'user-journeys', 'journeys', query),
      },
    },
  },

  // ========================= CALLS ========================
  calls: {
    ...createIndexesKeys('calls'),
    token: (query) => key('calls', 'token', query),
  },

  // ===================== PLATFORM =====================
  platform: {
    soaps: {
      configuration: {
        ...createIndexesKeys('platform', 'soaps', 'configuration'),
        id: (id) => ({
          details: key('platform', 'soaps', 'configuration', id, 'details'),
        }),
      },
    },
    settings: {
      list: key('platform', 'settings'),
    },
    constants: {
      list: key('platform', 'constants'),
      id: (constantId) => ({
        details: key('platform', 'constants', constantId, 'details'),
      }),
    },
    features: {
      list: key('platform', 'features'),
      id: (flagId) => ({
        details: key('platform', 'features', flagId, 'details'),
      }),
    },
    notifications: {
      ...createIndexesKeys('platform', 'notifications'),
      id: (notificationId) => ({
        details: key('platform', 'notifications', notificationId, 'details'),
      }),
    },
    blackouts: createIndexesKeys('platform', 'blackouts'),
    businessHours: key('platform', 'business-hours'),
    states: key('platform', 'states'),
  },
  heartbeat: {
    check: key('heartbeat', 'check'),
  },
  theme: {
    all: key('theme'),
    details: (query) => key('theme', 'details', query),
  },

  // ==================== ENCOUNTER TYPES ====================
  encounterTypes: {
    id: (encounterTypeId) => ({
      details: key('encounter-types', encounterTypeId, 'details'),
      bookingConfig: (query) => key('encounter-types', encounterTypeId, 'booking-config', query),
    }),
  },

  // ==================== PROVIDER TYPES ====================
  providerTypes: createIndexesKeys('provider-types'),

  // ======================= Q PHRASES ======================
  qPhrases: createIndexesKeys('q-phrases'),

  // ========================= SOPS =====================
  sops: {
    ...createIndexesKeys('sops'),
    id: (sopId) => ({
      details: key('sops', sopId, 'details'),
    }),
  },

  notificationLogs: createIndexesKeys('notification-logs'),

  reportLogs: createIndexesKeys('report-logs'),

  dohFaxNumbers: createIndexesKeys('doh-fax-numbers'),

  // ======================== TASKS ========================
  tasks: {
    ...createIndexesKeys('tasks'),
    id: (taskId) => ({
      details: key('tasks', taskId, 'details'),
      notes: createIndexesKeys('tasks', taskId, 'notes'),
      documents: createIndexesKeys('tasks', taskId, 'documents'),
    }),
  },

  // =================== ESCALATION TASKS ===================
  escalationTasks: {
    ...createIndexesKeys('escalation-tasks'),
    id: (taskId) => ({
      details: key('escalation-tasks', taskId, 'details'),
    }),
  },

  // ===================== APPOINTMENTS =====================
  appointments: {
    ...createIndexesKeys('appointments'),
    reports: {
      conflicts: key('appointments', 'reports', 'conflicts'),
    },
    onDemand: {
      ...createIndexesKeys('appointments', 'on-demand'),
      id: (onDemandLogId) => ({
        details: key('appointments', 'on-demand', onDemandLogId, 'details'),
      }),
    },
    asyncMessaging: {
      ...createIndexesKeys('appointments', 'async-messaging'),
      id: (asyncMessagingLogId) => ({
        details: key('appointments', 'async-messaging', asyncMessagingLogId, 'details'),
      }),
    },
    id: (appointmentId) => ({
      details: key('appointments', appointmentId, 'details'),
      notes: createIndexesKeys('appointments', appointmentId, 'notes'),
      labs: createIndexesKeys('appointments', appointmentId, 'labs'),
      prescriptions: createIndexesKeys('appointments', appointmentId, 'prescriptions'),
      asyncAssessments: key('appointments', appointmentId, 'async_assessments'),
      preview: {
        soapNote: key('appointments', appointmentId, 'preview', 'soap_note'),
        avs: key('appointments', appointmentId, 'preview', 'avs'),
      },
    }),
  },

  // =================== CONVERSATIONS ===================
  conversations: {
    ...createIndexesKeys('conversations'),
    betweenUsers: (query) => key('conversations', 'between-users', query),
    search: (query) => key('conversations', 'search', query),
    id: (conversationId) => ({
      details: key('conversations', conversationId, 'details'),
      messages: {
        ...createIndexesKeys('conversations', conversationId, 'messages'),
        id: (messageId) => ({
          details: key('conversations', conversationId, 'messages', messageId, 'details'),
        }),
      },
      behalfUsers: createIndexesKeys('conversations', conversationId, 'behalf-of-users'),
      users: {
        search: createIndexesKeys('conversations', conversationId, 'users', 'search'),
      },
    }),
  },

  // ===================== PROVIDERS =====================
  providers: {
    ...createIndexesKeys('providers'),
    ranks: createIndexesKeys('providers', 'ranks'),
    availabilitiesSummary: createIndexesKeys('providers', 'availabilities-summary'),
    assignments: createIndexesKeys('providers', 'assignments'),
    activeCensus: createIndexesKeys('providers', 'active-census'),
    availabilityGaps: {
      daily: createIndexesKeys('providers', 'availability-gaps', 'daily'),
      weekly: createIndexesKeys('providers', 'availability-gaps', 'weekly'),
    },
    id: (providerId) => ({
      details: key('providers', providerId, 'details'),
      metrics: createIndexesKeys('providers', providerId, 'metrics'),
      cbos: createIndexesKeys('providers', providerId, 'cbos'),
      licenses: {
        ...createIndexesKeys('providers', providerId, 'licenses'),
        id: (licenseId) => ({
          details: key('providers', providerId, 'licenses', licenseId, 'details'),
          notes: createIndexesKeys('providers', providerId, 'licenses', licenseId, 'notes'),
        }),
      },
      availabilities: {
        ...createIndexesKeys('providers', providerId, 'availabilities'),
        months: createIndexesKeys('providers', providerId, 'availabilities', 'months'),
      },
      appointments: createIndexesKeys('providers', providerId, 'appointments'),
      onDemandAppointments: createIndexesKeys('providers', providerId, 'on-demand-appointments'),
      adHocAppointments: createIndexesKeys('providers', providerId, 'ad-hoc-appointments'),
      completedEncounters: createIndexesKeys('providers', providerId, 'completed-encounters'),
      dashboard: {
        labs: createIndexesKeys('providers', providerId, 'dashboard', 'labs'),
        appointments: createIndexesKeys('providers', providerId, 'dashboard', 'appointments'),
        assessments: createIndexesKeys('providers', providerId, 'dashboard', 'assessments'),
        parTasks: createIndexesKeys('providers', providerId, 'dashboard', 'par-tasks'),
        licenseTasks: createIndexesKeys('providers', providerId, 'dashboard', 'license-tasks'),
      },
    }),
  },

  // ===================== CBOS =====================
  cbos: {
    ...createIndexesKeys('cbos'),
    coverage: key('cbos', 'coverage'),
    id: (cboId) => ({
      details: key('cbos', cboId, 'details'),
      ruleSets: key('cbos', cboId, 'rule-sets'),
      coverage: createIndexesKeys('cbos', cboId, 'coverage'),
      notes: createIndexesKeys('cbos', cboId, 'notes'),
      prescriptionFileSettings: createIndexesKeys('cbos', cboId, 'prescription-file-settings'),
      metrics: createIndexesKeys('cbos', cboId, 'metrics'),
      providers: {
        ...createIndexesKeys('cbos', cboId, 'providers'),
        ranks: createIndexesKeys('cbos', cboId, 'providers', 'ranks'),
        possibleDefault: createIndexesKeys('cbos', cboId, 'providers', 'possible-default'),
      },
      gaps: {
        availability: createIndexesKeys('cbos', cboId, 'gaps', 'availability'),
        weekly: createIndexesKeys('cbos', cboId, 'gaps', 'weekly'),
      },
      contractedPharmacies: createIndexesKeys('cbos', cboId, 'contracted-pharmacies'),
      patients: {
        former: {
          ...createIndexesKeys('cbos', cboId, 'former-patients'),
          id: (patientId) => ({
            details: key('cbos', cboId, 'former-patients', patientId, 'details'),
            encounters: createIndexesKeys('cbos', cboId, 'former-patients', patientId, 'encounters'),
          }),
        },
      },
    }),
  },

  // =================== PHARMACIES ===================
  pharmacies: {
    ...createIndexesKeys('pharmacies'),
    tasks: {
      ...createIndexesKeys('pharmacies', 'tasks'),
      details: key('pharmacies', 'tasks', 'details'),
      id: (taskId) => ({
        details: key('pharmacies', 'tasks', 'details', taskId),
      }),
    },
    id: (pharmacyId) => ({
      details: key('pharmacies', pharmacyId, 'details'),
    }),
  },

  // ===================== LABS =====================
  labs: {
    ...createIndexesKeys('labs'),
    id: (labId) => ({
      details: key('labs', labId, 'details'),
      canReview: key('labs', labId, 'can-review'),
      notes: createIndexesKeys('labs', labId, 'notes'),
    }),
    unmatched: {
      ...createIndexesKeys('labs', 'unmatched'),
      id: (labId) => ({
        details: key('labs', 'unmatched', labId, 'details'),
      }),
    },
  },

  // ==================== UNMATCHED LABS ===================
  unmatchedLabs: {
    ...createIndexesKeys('unmatched-labs'),
    id: (labId) => ({
      details: key('unmatched-labs', labId, 'details'),
    }),
  },

  // ===================== PRESCRIPTIONS =====================
  prescriptions: {
    ...createIndexesKeys('prescriptions'),
    report: key('prescriptions', 'report'),
    id: (prescriptionId) => ({
      details: key('prescriptions', prescriptionId, 'details'),
      notes: createIndexesKeys('prescriptions', prescriptionId, 'notes'),
    }),
  },

  // ===================== ASSESSMENTS =====================
  assessments: {
    ...createIndexesKeys('assessments'),
    pending: createIndexesKeys('assessments', 'pending'),
    id: (assessmentId) => ({
      details: key('assessments', assessmentId, 'details'),
      notes: createIndexesKeys('assessments', assessmentId, 'notes'),
    }),
  },

  // ===================== LAB REQUESTS =====================
  labRequests: {
    ...createIndexesKeys('lab_requests'),
    id: (labRequestId) => ({
      details: key('lab_requests', labRequestId, 'details'),
    }),
  },

  // ===================== LHC DASHBOARD =====================
  lhcDashboard: {
    ...createIndexesKeys('lhc-dashboard'),
    id: (summaryId) => ({
      details: key('lhc-dashboard', summaryId, 'details'),
      notes: createIndexesKeys('lhc-dashboard', summaryId, 'notes'),
    }),
  },

  // ===================== USERS =====================
  users: {
    ...createIndexesKeys('users'),
    id: (userId) => ({
      details: key('users', userId, 'details'),
      card: key('users', userId, 'card'),
      metrics: createIndexesKeys('users', userId, 'metrics'),
      mentions: createIndexesKeys('users', userId, 'mentions'),
      adminTimes: createIndexesKeys('users', userId, 'admin-time'),
      outOfOfficeTimes: {
        ...createIndexesKeys('users', userId, 'out-of-office-time'),
        id: (outOfOfficeId) => ({
          notes: createIndexesKeys('users', userId, 'out-of-office-time', outOfOfficeId, 'notes'),
        }),
      },
      indicator: (type) => createIndexesKeys('users', userId, 'indicator', type),
      notifications: createIndexesKeys('users', userId, 'notifications'),
      sopNotifications: createIndexesKeys('users', userId, 'sop-notifications'),
      pharmacies: createIndexesKeys('users', userId, 'pharmacies'),
      settings: createIndexesKeys('users', userId, 'settings'),
      tours: {
        ...createIndexesKeys('users', userId, 'tours'),
        id: (tourId) => ({
          details: key('users', userId, 'tours', tourId, 'details'),
        }),
      },
    }),
  },

  // ================== SYSTEM USERS =====================
  systemUsers: {
    ...createIndexesKeys('system-users'),
    id: (userId) => ({
      details: key('system-users', userId, 'details'),
    }),
  },

  // ==================== PATIENTS ====================
  patients: {
    ...createIndexesKeys('patients'),
    healthHistoryAlerts: createIndexesKeys('patients', 'health-history-alerts'),
    id: (patientId) => ({
      details: key('patients', patientId, 'details'),
      photoID: key('patients', patientId, 'photo-id'),
      photoIDHistory: key('patients', patientId, 'photo-id-history'),
      accessible: key('patients', patientId, 'accessible'),
      internalConversation: key('patients', patientId, 'internal-conversation'),
      consents: createIndexesKeys('patients', patientId, 'consents'),
      priorAuths: {
        glp1Options: key('patients', patientId, 'prior-auths', 'glp1-options'),
      },
      payments: {
        ...createIndexesKeys('patients', patientId, 'payments'),
        id: (paymentId) => ({
          details: key('patients', patientId, 'payments', paymentId, 'details'),
        }),
      },
      encounterTypes: {
        id: (encounterTypeId) => ({
          payments: {
            check: (query) => key('patients', patientId, 'encounter-types', encounterTypeId, 'payments', 'check', query),
          },
        }),
      },
      assessments: {
        ...createIndexesKeys('patients', patientId, 'assessments'),
        id: (assessmentId) => ({
          details: key('patients', patientId, 'assessments', assessmentId, 'details'),
        }),
        async: {
          id: (assessmentId) => ({
            details: key('patients', patientId, 'assessments-async', assessmentId, 'details'),
          }),
        },
      },
      todos: createIndexesKeys('patients', patientId, 'todos'),
      availabilities: createIndexesKeys('patients', patientId, 'availabilities'),
      schedulingEligibilities: {
        ...createIndexesKeys('patients', patientId, 'scheduling-eligibilities'),
        appointments: {
          id: (appointmentId) => ({
            details: key('patients', patientId, 'scheduling-eligibilities', 'appointments', appointmentId, 'details'),
          }),
        },
        id: (encounterTypeId) => ({
          details: key('patients', patientId, 'scheduling-eligibilities', encounterTypeId, 'details'),
        }),
      },
      quarterlyAutomations: createIndexesKeys('patients', patientId, 'quarterly-automations'),
      documents: {
        ...createIndexesKeys('patients', patientId, 'documents'),
        id: (documentId) => ({
          details: key('patients', patientId, 'documents', documentId, 'details'),
        }),
      },
      insurances: {
        ...createIndexesKeys('patients', patientId, 'insurances'),
        summary: key('patients', patientId, 'insurances', 'summary'),
        id: (insuranceId) => ({
          details: key('patients', patientId, 'insurances', insuranceId, 'details'),
        }),
      },
      prescriptions: {
        ...createIndexesKeys('patients', patientId, 'prescriptions'),
        id: (prescriptionId) => ({
          details: key('patients', patientId, 'prescriptions', prescriptionId, 'details'),
        }),
      },
      labs: {
        ...createIndexesKeys('patients', patientId, 'labs'),
        home: key('patients', patientId, 'labs', 'lists', 'home'),
        file: (fileId) => key('patients', patientId, 'labs', 'file', fileId),
        vendors: createIndexesKeys('patients', patientId, 'lab-vendors'),
        id: (labId) => ({
          details: key('patients', patientId, 'labs', labId, 'details'),
          notes: createIndexesKeys('patients', patientId, 'labs', labId, 'notes'),
        }),
      },
      roiMedicalRecords: {
        id: (medicalRecordId) => ({
          details: key('patients', patientId, 'roi-medical-records', medicalRecordId, 'details'),
        }),
      },
      events: createIndexesKeys('patients', patientId, 'events'),
      gilead: {
        id: (gileadApplicationId) => ({
          details: key('patients', patientId, 'gilead-applications', gileadApplicationId, 'details'),
        }),
      },
      roiRequest: {
        id: (roiRequestId) => ({
          details: key('patients', patientId, 'roi-requests', roiRequestId, 'details'),
        }),
      },
      cbos: {
        ...createIndexesKeys('patients', patientId, 'cbos'),
        id: (cboId) => ({
          verify: createIndexesKeys('patients', patientId, 'cbos', cboId, 'verify'),
        }),
      },
      providers: createIndexesKeys('patients', patientId, 'providers'),
      notes: createIndexesKeys('patients', patientId, 'notes'),
      priorityNotes: createIndexesKeys('patients', patientId, 'priority-notes'),
      tasks: {
        ...createIndexesKeys('patients', patientId, 'tasks'),
        id: (taskId) => ({
          details: key('patients', patientId, 'tasks', taskId, 'details'),
          notes: createIndexesKeys('patients', patientId, 'tasks', taskId, 'notes'),
        }),
      },
      appointments: {
        ...createIndexesKeys('patients', patientId, 'appointments'),
        home: key('patients', patientId, 'appointments', 'lists', 'home'),
        id: (appointmentId) => ({
          details: key('patients', patientId, 'appointments', appointmentId, 'details'),
          soapSchema: key('patients', patientId, 'appointments', appointmentId, 'soap-schema'),
          payments: {
            check: (query) => key('patients', patientId, 'appointments', appointmentId, 'payments', 'check', query),
          },
        }),
      },
      encounters: {
        ...createIndexesKeys('patients', patientId, 'encounters'),
        available: createIndexesKeys('patients', patientId, 'encounters', 'available'),
        id: (encounterId) => ({
          details: key('patients', patientId, 'encounters', encounterId, 'details'),
          notes: createIndexesKeys('patients', patientId, 'encounters', encounterId, 'notes'),
          vaccines: createIndexesKeys('patients', patientId, 'encounters', encounterId, 'vaccines'),
          appointments: createIndexesKeys('patients', patientId, 'encounters', encounterId, 'appointments'),
          availabilities: createIndexesKeys('patients', patientId, 'encounters', encounterId, 'availabilities'),
          labs: createIndexesKeys('patients', patientId, 'encounters', encounterId, 'labs'),
          prescriptions: createIndexesKeys('patients', patientId, 'encounters', encounterId, 'prescriptions'),
          referrals: createIndexesKeys('patients', patientId, 'encounters', encounterId, 'referrals'),
          assessments: createIndexesKeys('patients', patientId, 'encounters', encounterId, 'assessments'),
          morbidities: createIndexesKeys('patients', patientId, 'encounters', encounterId, 'doh-morbidities'),
        }),
      },
      healthHistory: {
        alerts: createIndexesKeys('patients', patientId, 'health-history', 'alerts'),
        type: (type) => createIndexesKeys('patients', patientId, 'health-history', type),
        notes: createIndexesKeys('patients', patientId, 'health-history', 'notes'),
      },
    }),
  },

  // ===================== GAP REQUESTS =====================
  gapRequests: createIndexesKeys('gap-requests'),
  gapBoard: {
    providers: createIndexesKeys('gap-board', 'providers'),
  },

  // ===================== SCHEDULE SLOTS =====================
  scheduleSlots: {
    logs: createIndexesKeys('schedule-slots', 'logs'),
    declined: createIndexesKeys('schedule-slots', 'declined'),
    providers: {
      ...createIndexesKeys('schedule-slots', 'providers'),
      scheduled: {
        on: createIndexesKeys('schedule-slots', 'providers', 'scheduled-on'),
        off: createIndexesKeys('schedule-slots', 'providers', 'schedule-off'),
        ooo: createIndexesKeys('schedule-slots', 'providers', 'out-of-office'),
      },
      unassignedQhaLifestyleMeds: createIndexesKeys('schedule-slots', 'providers', 'unassigned-qha-lifestyle-meds'),
      id: (providerId) => ({
        pending: createIndexesKeys('schedule-slots', 'providers', providerId, 'pending'),
      }),
    },
    users: {
      scheduled: {
        on: createIndexesKeys('schedule-slots', 'users', 'scheduled-on'),
        off: createIndexesKeys('schedule-slots', 'users', 'scheduled-off'),
        ooo: createIndexesKeys('schedule-slots', 'users', 'out-of-office'),
      },
    },
  },
}

export default QueryKeys
